import { useSelector } from "react-redux";
import { ArrowRightIcon, ChevronRightIcon } from "@app/design-system/icons";
import { RootState } from "@app/store";
import { t } from "@app/utils/translation";
import { DriverCard } from "@app/features/shared/DriverCard";
import { LastEventCard } from "@app/features/shared/LastEventCard";
import { statusToneColor } from "@app/features/shared/statusTone/statusToneColor";
import { isCallDisabled } from "@app/features/shared/statusTone/isCallDisabled";
import { computeTrackingStatusLabel } from "@app/features/shared/statusTone/computeTrackingStatusLabel";
import { DropoffSection } from "./DropoffSection";
import { BottomPanelProps } from "./BottomPanel.types";
import styles from "./BottomPanel.module.css";

/**
 * The Home page's floating bottom sheet — Order Status, a middle section,
 * Order Details. Rendered through `PageShell`'s `bottomBar` slot in
 * `variant="map"` mode (see HomePage.tsx), which is what pins it to the
 * bottom edge, floating above the map, rather than anything in this
 * component itself.
 *
 * The middle section is Driver Information for Ondemand orders, or the
 * order's Last Event for every other kind — never both, and never an empty
 * reserved gap when neither is available yet (e.g. no driver assigned yet,
 * or the event history hasn't loaded). Both cards share the same
 * `driverRow` slot/divider so the panel looks identical either way.
 *
 * In dropoff-tracking mode, a `DropoffSection` always follows that same
 * slot — unlike the driver/last-event section, it never depends on a
 * driver being assigned — and the status label switches to
 * `computeTrackingStatusLabel`'s pickup/delivery-progress copy.
 */
export const BottomPanel = ({
  statusLabel,
  statusCode,
  statusTone,
  orderKind,
  driver,
  lastEvent,
  isDropoffTracking,
  dropoff,
  remainingQueuePosition,
  onOrderDetailsClick,
  onDropoffClick,
}: BottomPanelProps) => {
  const language = useSelector((state: RootState) => state.setting.language);

  const middleContent =
    orderKind === "ondemand" ? (
      driver ? (
        <DriverCard driver={driver} className={styles.driverRow} callDisabled={isCallDisabled(statusCode)} />
      ) : null
    ) : lastEvent ? (
      <LastEventCard event={lastEvent} className={styles.driverRow} />
    ) : null;

  const displayedStatusLabel = computeTrackingStatusLabel({
    isDropoffTracking,
    statusCode,
    remainingQueuePosition,
    dropoffStatus: dropoff?.status ?? null,
    fallbackLabel: statusLabel,
    language,
  });

  return (
    <div className={styles.panel}>
      <div className={styles.statusSection}>
        <span className={styles.statusLabel} style={{ color: statusToneColor(statusTone) }}>
          {displayedStatusLabel}
        </span>
      </div>

      {middleContent ? (
        <>
          <div className={styles.divider} />
          {middleContent}
        </>
      ) : null}

      {isDropoffTracking && dropoff ? (
        <>
          <div className={styles.divider} />
          <DropoffSection
            address={dropoff.address}
            completedAt={dropoff.completedAt}
            description={dropoff.description}
            detailsCount={dropoff.detailsCount}
            onClick={onDropoffClick}
            className={styles.driverRow}
          />
        </>
      ) : null}

      <div className={styles.divider} />
      <button type="button" className={styles.orderDetailsButton} onClick={onOrderDetailsClick}>
        {t("OrderDetails", language)}
        <ArrowRightIcon size={20} />
      </button>
      <div className={styles.divider} />
    </div>
  );
};
